<template>
  <div class="hr-page" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container">
        <!-- Modern page header -->
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">{{ title }}</h1>
              <p v-if="subtitle" class="dashboard-subtitle">
                <span class="icon" v-if="icon">{{ icon }}</span>
                {{ subtitle }}
              </p>
            </div>
            <div class="header-actions">
              <slot name="header-actions"></slot>
            </div>
          </div>
          <div class="breadcrumb-section">
            <nav class="breadcrumb" aria-label="Breadcrumb">
              <span v-for="(item, index) in breadcrumbItems" :key="index" class="breadcrumb-item">
                <button
                  v-if="item.route || item.url"
                  type="button"
                  class="breadcrumb-link"
                  @click="handleBreadcrumbClick(item)"
                >
                  {{ item.label }}
                </button>
                <span v-else :class="['breadcrumb-text', { 'breadcrumb-current': index === breadcrumbItems.length - 1 }]">
                  {{ item.label }}
                </span>
                <span v-if="index < breadcrumbItems.length - 1" class="breadcrumb-separator">/</span>
              </span>
            </nav>
          </div>
          
          <!-- Optional Tabs Slot (attached seamlessly to the header) -->
          <div class="header-tabs-container" v-if="$slots.tabs">
            <slot name="tabs"></slot>
          </div>
        </div>

        <!-- Main content slot -->
        <div class="hr-page-content">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/Navigation.vue";

export default {
  name: "HrPageLayout",
  components: { Navigation },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "" },
    breadcrumbItems: {
      type: Array,
      default: () => [{ label: "HR Management" }]
    }
  },
  data() {
    return {
      isNavCollapsed: false
    };
  },
  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    handleBreadcrumbClick(item) {
      if (item.route) {
        this.$router.push(item.route);
      } else if (item.url) {
        window.location.href = item.url;
      }
    },
    goHome() {
      this.$router.push("/dashboard");
    }
  }
};
</script>

<style scoped>
/* Pure Vue layout: no Bootstrap. Content offset must match Navigation width (280px / 70px collapsed). */
.hr-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  box-sizing: border-box;
}

.hr-page.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  /* Ensure content never goes under the fixed nav */
  position: relative;
  z-index: 1;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.modern-page-header {
  background: #ffffff;
  border-radius: 20px;
  padding: 25px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: visible; /* allow dropdowns in header-actions to show below */
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.header-tabs-container {
  margin: 15px -30px -25px -30px;
  padding: 0 30px;
}

.title-section {
  flex: 1;
  min-width: 300px;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 15px;
  margin: 8px 0 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  min-height: 24px;
}

.breadcrumb-link,
.breadcrumb-text {
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 0;
  margin: 0;
}

.breadcrumb-link {
  color: #475569;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: #4f46e5;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

.hr-page-content {
  margin-bottom: 30px;
}

@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 15px;
  }
  .hr-page.nav-collapsed .dashboard-content {
    margin-left: 0;
  }
  .dashboard-title {
    font-size: 22px;
  }
}
</style>
